import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MonitorPlay,
  GraduationCap,
  Sheet,
  PenLine,
  Newspaper,
  Compass,
  Clapperboard,
  Megaphone,
} from "lucide-react";
import { VIDEOS } from "../../data/content";
import useReveal from "../shared/useReveal";
import "./LearnFaq.css";
import "./HomeSections.css";

const LEARN_PROGRAMS = [
  {
    icon: MonitorPlay,
    title: "FinLab — Learn Stock Market Live",
    desc: "Derivatives, technical & fundamental analysis, mutual funds and cryptocurrency — taught live by industry experts, with live trading experience on NSE terminals. Get certified.",
    href: "/learning-centre",
    cta: "Join FinLab →",
  },
  {
    icon: GraduationCap,
    title: "CFA Preparation — Learn From CFAs",
    desc: "Structured CFA Level I/II/III prep with live classes by Chartered Financial Analysts, peer networking, mentoring, real-world case studies and mock exams.",
    href: "/learning-centre",
    cta: "Start CFA Prep →",
  },
  {
    icon: Sheet,
    title: "Tallex — Tally + Excel Certification",
    desc: "Practical Tally and MS Excel training with certification to boost your resume — live from industry experts. Great for finance students and professionals.",
    href: "/learning-centre",
    cta: "Get Certified →",
  },
];

const LEARN_LINKS = [
  {
    icon: PenLine,
    title: "Blogs",
    desc: "Plain-language articles on investing, tax & business finance.",
    href: "https://blog.poonjimitra.com",
    external: true,
  },
  {
    icon: Newspaper,
    title: "Market Updates",
    desc: "What moved the markets — and what it means for you.",
    href: "/blogs",
  },
  {
    icon: Compass,
    title: "Investment Guides",
    desc: "First SIP, first bond, first business loan — step by step.",
    href: "/learning-centre",
  },
  {
    icon: Clapperboard,
    title: "Videos",
    desc: "Concepts in Hindi + English, in minutes not hours.",
    href: "/learning-centre",
  },
  {
    icon: Megaphone,
    title: "Financial News",
    desc: "Curated news that affects your money — minus the noise.",
    href: "/blogs",
  },
];

const FAQS = [
  {
    q: "Is the first consultation really free?",
    a: "Yes — a 20-minute discovery call costs nothing and comes with no obligation. If we're not the right fit, we'll tell you honestly.",
  },
  {
    q: "How is Poonji Mitra different from apps like Groww or Angel One?",
    a: "Those platforms are excellent for self-directed execution. We're an advisory ecosystem — a CFA, CA and CS team that plans, selects, reviews and rebalances for you, and also handles your business finance, tax and insurance under the same roof.",
  },
  {
    q: "What do I need for a portfolio review?",
    a: "Just your CAMS or KFintech consolidated statement (or a PDF/Excel of holdings). You'll get your health score, risk analysis and a written action plan within 24 hours.",
  },
  {
    q: "Are my returns guaranteed?",
    a: "No — and be careful of anyone who says otherwise. All market-linked investments carry risk. What we guarantee is a disciplined, research-driven process, full transparency and quarterly reviews.",
  },
  {
    q: "Do you work with clients outside Jaipur?",
    a: "Yes. Onboarding is fully paperless and reviews happen over video calls — we serve clients across India.",
  },
];

export default function LearnFaq() {
  const ref = useReveal();
  const [open, setOpen] = useState(0);

  return (
    <section className="section alt" id="learn">
      <div className="container" ref={ref}>
        <div className="home-section-head section-head pm-reveal">
          <span className="kicker">Financial Literacy</span>
          <h2>We don&apos;t just manage your money. We help you understand it.</h2>
          <p className="sub">
            Three focused initiatives building a financially aware generation.
          </p>
        </div>

        <div className="home-learn-cards pm-stagger">
          {LEARN_PROGRAMS.map((p) => {
            const Icon = p.icon;
            return (
              <Link key={p.title} to={p.href} className="home-learn-program pm-reveal">
                <div className="home-learn-program-icon" aria-hidden="true">
                  <Icon size={22} strokeWidth={2.1} />
                </div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <span className="home-learn-program-go">{p.cta}</span>
              </Link>
            );
          })}
        </div>

        {VIDEOS?.length > 0 && (
          <>
            <h2 className="home-faq-title pm-reveal" style={{ fontSize: "1.4rem" }}>
              Watch & learn
            </h2>
            <div className="home-video-grid pm-stagger">
              {VIDEOS.map((v) => (
                <a
                  key={v.id}
                  className="home-video-card pm-reveal"
                  href={`https://www.youtube.com/watch?v=${v.youtubeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="home-video-thumb">
                    <img src={v.thumbnail} alt="" />
                    <span className="home-playbtn">▶</span>
                  </div>
                  <h3>{v.title}</h3>
                </a>
              ))}
            </div>
          </>
        )}

        <div className="home-learn-grid pm-stagger">
          {LEARN_LINKS.map((l) => {
            const Icon = l.icon;
            const className = "home-learn-card pm-reveal";
            const body = (
              <>
                <div className="home-learn-card-icon" aria-hidden="true">
                  <Icon size={18} strokeWidth={2.1} />
                </div>
                <h3>{l.title}</h3>
                <p>{l.desc}</p>
              </>
            );

            if (l.external) {
              return (
                <a
                  key={l.title}
                  href={l.href}
                  className={className}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {body}
                </a>
              );
            }

            return (
              <Link key={l.title} to={l.href} className={className}>
                {body}
              </Link>
            );
          })}
        </div>

        <h2 className="home-faq-title pm-reveal">Frequently asked questions</h2>
        <div className="home-faq">
          {FAQS.map((f, i) => (
            <div
              key={f.q}
              className={`home-faq-item${open === i ? " open" : ""}`}
            >
              <button
                type="button"
                className="home-faq-q"
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
              >
                {f.q}
              </button>
              <div className="home-faq-a">
                <p>{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
